<template>
  <template v-if="$route.name === 'settings.nationalities.index'">
    <NationalitiesFilter @search="onSearch" @add="gotoAdd" />

    <NationalitiesTable
      :items="items"
      :loading="loading"
      @delete="confirmDelete"
    />

    <PaginationControls
      v-if="!loading && pagination.total > 0"
      :pagination="{
        currentPage: pagination.currentPage,
        perPage: pagination.perPage,
        totalItems: pagination.total,
        totalPages: pagination.lastPage,
        from: pagination.from,
        to: pagination.to
      }"
      @page-changed="fetchList"
    />

    <BaseDialog
      ref="del"
      :title="t('common.delete')"
      confirm-variant="danger"
      :confirm-text="t('common.delete')"
      :loading="delLoading"
      @confirm="doDelete"
    >
      <template #body>
        <p>
          {{ t('settings.nationalities.deleteConfirm') }}
          <strong>{{ selectedName }}</strong>
        </p>
      </template>
    </BaseDialog>
  </template>

  <router-view />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import NationalitiesFilter from '@/components/features/nationalities/NationalitiesFilter.vue'
import NationalitiesTable from '@/components/features/nationalities/NationalitiesTable.vue'
import PaginationControls from '@/components/common/PaginationControls.vue'
import BaseDialog from '@/components/common/BaseDialog.vue'
import api from '@/api'
import { useToast } from 'vue-toastification'

const { t, locale } = useI18n()
const router = useRouter()
const toast = useToast()

const loading = ref(false)
const items = ref([])
const pagination = ref({ currentPage: 1, perPage: 10, total: 0, lastPage: 1, from: 0, to: 0 })
const search = ref('')

const del = ref(null)
const delLoading = ref(false)
const selected = ref(null)

const selectedName = computed(() => {
  const l = (locale.value || 'ar').split('-')[0]
  return selected.value?.name_translations?.[l] || selected.value?.name || ''
})

const getLocalizedMessage = (message, fallback = '') => {
  if (!message) return fallback
  if (typeof message === 'string') return message

  const lang = (locale.value || 'ar').split('-')[0]

  if (typeof message === 'object') {
    return message[lang] || message.ar || message.en || fallback
  }

  return fallback
}

const fetchList = async (page = pagination.value.currentPage) => {
  loading.value = true
  try {
    const { data } = await api.get('/nationalities', {
      params: { page, per_page: pagination.value.perPage, search: search.value }
    })

    items.value = data.data || []

    const m = data.meta || {}
    pagination.value = {
      currentPage: m.current_page ?? page,
      perPage: m.per_page ?? pagination.value.perPage,
      total: m.total ?? items.value.length,
      lastPage: m.last_page ?? 1,
      from: m.from ?? 0,
      to: m.to ?? items.value.length
    }
  } catch (e) {
    toast.error(
      getLocalizedMessage(e?.response?.data?.message, t('common.failedLoad'))
    )
  } finally {
    loading.value = false
  }
}

const onSearch = (s) => {
  search.value = s
  fetchList(1)
}

const gotoAdd = () => router.push({ name: 'settings.nationalities.create' })

const confirmDelete = (row) => {
  selected.value = row
  del.value?.openDialog()
}

const doDelete = async () => {
  if (!selected.value) return

  delLoading.value = true

  try {
    const { data } = await api.delete(`/nationalities/${selected.value.id}`)

    toast.success(
      getLocalizedMessage(data?.message, t('common.deleted'))
    )

    del.value?.closeDialog()
    selected.value = null

    const isLastOnPage = items.value.length === 1 && pagination.value.currentPage > 1
    await fetchList(isLastOnPage ? pagination.value.currentPage - 1 : pagination.value.currentPage)
  } catch (e) {
    toast.error(
      getLocalizedMessage(e?.response?.data?.message, t('common.deleteFailed'))
    )
  } finally {
    delLoading.value = false
  }
}

fetchList()
</script>